/**
 * Costume closet — shared loaner pieces dancers can borrow (not year-scoped).
 * Photos and descriptions get filled in over time.
 */

export { IABF_COSTUME_CLOSET_COLLECTION } from './iabf-event'

export const CLOSET_STORAGE_PREFIX = 'iabf-costume-closet'

export const CLOSET_CATEGORIES = [
  { id: 'character', label: 'Character & extras' },
  { id: 'prop', label: 'Prop' },
  { id: 'hats', label: 'Hats' },
  { id: 'veils', label: 'Veils' },
  { id: 'headpieces', label: 'Headpieces & crowns' },
  { id: 'tiaras', label: 'Tiaras' },
  { id: 'menswear', label: 'Men’s pieces' },
  { id: 'tutus', label: 'Romantic tutus' }
]

export function closetCategoryLabel (id) {
  const c = CLOSET_CATEGORIES.find(x => x.id === id)
  return c ? c.label : (id || 'Other')
}

/** Default borrow fee (USD cents). Tutus $15; everything else $5. Override per item with priceCents. */
export const CLOSET_PRICE_TUTU_CENTS = 1500
export const CLOSET_PRICE_ACCESSORY_CENTS = 500

export function defaultClosetPriceCents (category) {
  return category === 'tutus' ? CLOSET_PRICE_TUTU_CENTS : CLOSET_PRICE_ACCESSORY_CENTS
}

export function closetItemPriceCents (item) {
  if (item && item.priceCents != null && item.priceCents !== '') {
    const n = Math.round(Number(item.priceCents))
    if (Number.isFinite(n) && n >= 0) {
      return n
    }
  }
  return defaultClosetPriceCents(item && item.category)
}

export function formatClosetPrice (cents) {
  const n = Math.round(Number(cents) || 0)
  if (n === 0) {
    return 'Free'
  }
  return '$' + (n / 100).toFixed(n % 100 === 0 ? 0 : 2)
}

export const CLOSET_LOAN_ACTIVE = ['reserved', 'checked-out']

export function closetLoanDancerName (loan) {
  return String((loan && loan.dancerName) || '').trim()
}

/** Active borrowers for a closet piece this year (first + last as stored on the loan). */
export function closetItemBorrowers (item, loans) {
  if (!item || !item.id) {
    return []
  }
  const names = []
  const seen = new Set()
  ;(loans || []).forEach(loan => {
    if (loan.itemId !== item.id) {
      return
    }
    if (CLOSET_LOAN_ACTIVE.indexOf(loan.status) === -1) {
      return
    }
    const name = closetLoanDancerName(loan)
    if (!name) {
      return
    }
    const key = name.toLowerCase()
    if (seen.has(key)) {
      return
    }
    seen.add(key)
    names.push(name)
  })
  return names
}

/** e.g. "borrowed by Joyce Sanders in 2027" */
export function closetBorrowNote (item, loans, year) {
  const names = closetItemBorrowers(item, loans)
  if (!names.length) {
    return ''
  }
  const y = Number(year)
  const yearBit = Number.isFinite(y) && y > 0 ? (' in ' + y) : ''
  return 'borrowed by ' + names.join(', ') + yearBit
}

/**
 * Seed from the handwritten closet list (IMG_7700).
 * Descriptions are placeholders until photos land.
 */
export const CLOSET_SEED_ITEMS = [
  {
    name: 'Blue suspenders',
    category: 'character',
    qty: 1,
    description: 'Classic blue suspenders — a fun extra for character or men’s looks.'
  },
  {
    name: 'Silver wand',
    category: 'character',
    qty: 1,
    description: 'A silver wand for fairy, sylph, or party scenes.'
  },
  {
    name: 'Rose hair comb',
    category: 'character',
    qty: 1,
    description: 'A rose hair comb. The list pairs it with the Anonymous look.'
  },
  {
    name: 'Pink fan',
    category: 'character',
    qty: 1,
    description: 'A pink folding fan. Listed with the Phantom pieces.'
  },
  {
    name: 'Silver masks',
    category: 'character',
    qty: null,
    description: 'Silver masks for masquerade or Phantom character work. Count TBD when we photograph them.'
  },
  {
    name: 'Geisha pieces',
    category: 'character',
    qty: 1,
    description: 'Character pieces labeled Geisha. We’ll add a clearer name and photo soon.'
  },
  {
    name: 'Bloom tutu with pins & bow',
    category: 'character',
    qty: 1,
    description: 'A bloom-style tutu, with two extra pins and a bow.'
  },
  {
    name: 'Swan ears',
    category: 'character',
    qty: 1,
    description: 'Swan ear headpiece, with two extra pins for securing.'
  },
  {
    name: 'Tie',
    category: 'character',
    qty: 1,
    description: 'A tie — listed with the swan pieces. Photo will confirm color and style.'
  },
  {
    name: 'Lace pink parasol',
    category: 'character',
    qty: 1,
    description: 'A lace pink parasol — pretty for photos or a character walk-on.'
  },
  {
    name: 'Red top hat',
    category: 'hats',
    qty: 1,
    description: 'A red top hat for character or comic bits.'
  },
  {
    name: 'Cape',
    category: 'character',
    qty: 1,
    description: 'A cape. We’ll add color and fabric once it’s photographed.'
  },
  {
    name: 'Top hats',
    category: 'hats',
    qty: 8,
    description: 'A set of top hats for character corps, photos, or comic moments.'
  },
  {
    name: 'Giselle veils',
    category: 'veils',
    qty: 15,
    description: 'Romantic wedding veils in the Giselle style — long, airy, ready to pin.'
  },
  {
    name: 'Shade headpieces — yellow',
    category: 'headpieces',
    qty: 8,
    description: 'Yellow shade / sunburst headpieces. Light and easy to pin.'
  },
  {
    name: 'Shade headpieces — red',
    category: 'headpieces',
    qty: 12,
    description: 'Red shade / sunburst headpieces. Light and easy to pin.'
  },
  {
    name: 'Shade headpieces — blue',
    category: 'headpieces',
    qty: 9,
    description: 'Blue shade / sunburst headpieces. Light and easy to pin.'
  },
  {
    name: 'Shade headpieces — green',
    category: 'headpieces',
    qty: 7,
    description: 'Green shade / sunburst headpieces. Light and easy to pin.'
  },
  {
    name: 'Shade headpieces — pink',
    category: 'headpieces',
    qty: 3,
    description: 'Pink shade / sunburst headpieces. Light and easy to pin.'
  },
  {
    name: 'Flower crowns',
    category: 'headpieces',
    qty: 2,
    description: 'Two flower crowns — soft and romantic for photos or corps.'
  },
  {
    name: 'Poet’s blouses',
    category: 'menswear',
    qty: 2,
    description: 'Two poet’s blouses for men’s character looks (or anyone who wants the romance).'
  },
  {
    name: 'Leaf crowns',
    category: 'headpieces',
    qty: 18,
    description: 'A set of leaf crowns — woodland, sylph, or garden looks.'
  },
  {
    name: 'Swan headband',
    category: 'headpieces',
    qty: 1,
    description: 'A swan headband to pair with the swan ears, or wear on its own.'
  },
  {
    name: 'Tiara — purple',
    category: 'tiaras',
    qty: 1,
    description: 'A purple tiara. We’ll add a close-up so you can see the sparkle.'
  },
  {
    name: 'Tiara — pink',
    category: 'tiaras',
    qty: 1,
    description: 'A pink tiara for princess or romantic solos.'
  },
  {
    name: 'Tiara — white (XL)',
    category: 'tiaras',
    qty: 1,
    description: 'An extra-large white tiara — big presence for the stage.'
  },
  {
    name: 'Crown (round)',
    category: 'tiaras',
    qty: 1,
    description: 'A round crown — fuller than a tiara, nice for royal character.'
  },
  {
    name: 'Tiara — black',
    category: 'tiaras',
    qty: 1,
    description: 'A black tiara. Dramatic for swan, villain, or contemporary romantic.'
  },
  {
    name: 'Pink romantic tutu (unlabeled)',
    category: 'tutus',
    qty: 1,
    description: 'A pink romantic tutu. No name on the label yet — we’ll photograph and size it.'
  },
  {
    name: 'Pink romantic tutu (my size)',
    category: 'tutus',
    qty: 1,
    description: 'A pink romantic tutu, noted as “my size” on the list. Photo and measurements coming.'
  },
  {
    name: 'Blue romantic tutu (Daria)',
    category: 'tutus',
    qty: 1,
    description: 'A blue romantic tutu labeled Daria. Soft tulle, below-the-knee romantic length.'
  },
  {
    name: 'Romantic tutu (Anie / Hayden)',
    category: 'tutus',
    qty: 1,
    description: 'A romantic tutu labeled Anie / Hayden. We’ll confirm color and size with a photo.'
  }
]
